import { Icon } from '@octano/global-ui';
import { useState } from 'react';
import { Input, PopoverBody, UncontrolledPopover } from 'reactstrap';

import IconButton from '../../../../components/buttons/IconButton';
import { formatDate } from '../../../../utils/dates';
import { OfferQuotaHistory } from '../types';

interface Props {
  aospvoId: number;
  initialQuota: number | null;
  pendingValue?: number;
  quotaHistories?: OfferQuotaHistory[];
  onChange?: (id: number, quota: number) => void;
  onCancel?: (id: number) => void;
  disabled?: boolean;
}

export function QuotaCell({
  aospvoId,
  initialQuota,
  pendingValue,
  quotaHistories,
  onChange,
  onCancel,
  disabled,
}: Props) {
  const initialValue = initialQuota ?? '';
  const [isEditing, setIsEditing] = useState(pendingValue !== undefined);
  const [value, setValue] = useState<string | number>(
    pendingValue ?? initialValue,
  );
  const popoverId = `quota-history-${aospvoId}`;

  const handleBlur = () => {
    const parsed = Number(value);
    const hasChanged =
      !isNaN(parsed) && parsed >= 0 && parsed !== Number(initialValue);
    if (hasChanged) {
      onChange?.(aospvoId, parsed);
    } else {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setValue(initialValue);
    onCancel?.(aospvoId);
    setIsEditing(false);
  };

  const iconColor = disabled ? 'disabled' : 'primary';

  const historyTooltip = quotaHistories && quotaHistories.length > 0 && (
    <>
      <span
        id={popoverId}
        className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary text-white fw-bold"
        style={{
          width: 18,
          height: 18,
          fontSize: 11,
          cursor: 'pointer',
          marginLeft: 10,
          marginRight: 5,
        }}
      >
        i
      </span>
      <UncontrolledPopover
        target={popoverId}
        placement="bottom"
        trigger="hover"
        delay={{ show: 0, hide: 150 }}
      >
        <PopoverBody style={{ textAlign: 'left', fontSize: 13 }}>
          <strong
            style={{
              backgroundColor: 'rgba(204,204,204,0.2)',
              display: 'block',
              margin: '-8px -12px 0',
              padding: '6px 12px',
              borderRadius: '4px 4px 0 0',
            }}
          >
            Historial cambios
          </strong>
          <hr style={{ margin: '6px 0' }} />
          {quotaHistories.map((h, i) => (
            <div key={i}>
              {h.quota} - {formatDate(h.createdAt, 'DD-MM-YYYY')} -{' '}
              {h.accountFullName}
            </div>
          ))}
        </PopoverBody>
      </UncontrolledPopover>
    </>
  );

  if (!isEditing) {
    return (
      <div className="d-flex align-items-center justify-content-center">
        <span>{value !== '' ? value : '-'}</span>
        {historyTooltip}
        <IconButton
          aria-label="edit-quota"
          onClick={() => setIsEditing(true)}
          disabled={disabled}
        >
          <Icon name="edit" size={16} color={iconColor} />
        </IconButton>
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center justify-content-center gap-2 p-0">
      <Input
        autoFocus
        type="number"
        min={0}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleBlur}
        style={{ width: 80, textAlign: 'center' }}
        className="border-primary"
        disabled={disabled}
      />
      {historyTooltip}
      <IconButton
        aria-label="cancel-quota"
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleCancel}
        disabled={disabled}
      >
        <Icon name="close" size={16} color={iconColor} />
      </IconButton>
    </div>
  );
}
